// The home page: each chain's last 24 hours, then every token that traded, ranked and filtered by
// GET /tokens (the screener mode), refreshed every 15 seconds while the tab is in view.
import { useEffect, useState, type ReactNode } from "react";
import { CHAIN_IDS, CHAINS, get, tokenHref, useGet, useNow, type ChainId, type ChainStatus, type FeeWindow, type TokenRow } from "./api";
import { age, compact, price, usd } from "./format";
import { A, Change, ChainMark, Icon, Launchpad, TokenLogo } from "./ui";

type Sort = "volume24hUsd" | "trades24h" | "fees" | "liquidityUsd" | "marketCapUsd" | "change24h" | "priceUsd" | "createdAt";

// "fees" sorts by the fees paid over the period picked in the toolbar.
const PRESETS: { name: string; hint?: string; sort: Sort; minFees?: number; filters: { minLiquidityUsd?: number; minVolume24hUsd?: number; excludeQuotes?: boolean } }[] = [
  {
    name: "Hot",
    hint: "Ranked by what traders paid in transaction fees over the period you pick. Tokens that paid under $1 in it are left out, and so are stablecoins and wrapped native coins.",
    sort: "fees",
    minFees: 1,
    filters: { excludeQuotes: true },
  },
  { name: "Trending", sort: "volume24hUsd", filters: {} },
  { name: "Gainers", sort: "change24h", filters: { minLiquidityUsd: 10_000, minVolume24hUsd: 10_000 } },
  { name: "New", sort: "createdAt", filters: {} },
  { name: "Market cap", sort: "marketCapUsd", filters: {} },
];

const LIQUIDITY = [0, 1_000, 10_000, 50_000, 100_000];
const FEE_PERIODS: [FeeWindow, string][] = [["5m", "5 min"], ["15m", "15 min"], ["30m", "30 min"], ["45m", "45 min"], ["1h", "1 hour"], ["6h", "6 hours"], ["24h", "24 hours"]];

/** Each chain's last 24 hours, and the chain picker: a tile filters the list below to its chain. */
function ChainStrip({ chain, onPick }: { chain: ChainId | "all"; onPick: (c: ChainId | "all") => void }) {
  const { data } = useGet<{ chains: ChainStatus[] }>("/chains", 15_000);
  const rows = CHAIN_IDS.flatMap((id) => data?.chains.find((c) => c.chain === id) ?? []);
  const sum = (k: "volumeUsd" | "trades" | "newTokens") => rows.reduce((n, c) => n + c.stats24h[k], 0);
  const total = sum("volumeUsd");
  return (
    <div className="chain-strip" role="group" aria-label="Chain">
      <ChainTile active={chain === "all"} onClick={() => onPick("all")} mark={<Icon name="layers" size={16} />} name="All chains" volume={data ? total : null} trades={sum("trades")} newTokens={sum("newTokens")} />
      {CHAIN_IDS.map((id) => {
        const c = rows.find((r) => r.chain === id);
        return (
          <ChainTile
            key={id}
            active={chain === id}
            onClick={() => onPick(id)}
            mark={<ChainMark chain={id} size={16} />}
            name={CHAINS[id].name}
            volume={c?.stats24h.volumeUsd ?? null}
            trades={c?.stats24h.trades ?? 0}
            traders={c?.stats24h.traders}
            newTokens={c?.stats24h.newTokens ?? 0}
            share={c && total ? c.stats24h.volumeUsd / total : undefined}
          />
        );
      })}
    </div>
  );
}

function ChainTile({ active, onClick, mark, name, volume, trades, traders, newTokens, share }: { active: boolean; onClick: () => void; mark: ReactNode; name: string; volume: number | null; trades: number; traders?: number; newTokens: number; share?: number }) {
  return (
    <button className={active ? "chain-tile active" : "chain-tile"} onClick={onClick} aria-pressed={active}>
      <span className="chain-tile-top">
        <span className="chain-tile-name">
          {mark}
          <span className="ellipsis">{name}</span>
        </span>
        <span className="chain-tile-volume num" title="Volume, last 24 hours">
          {usd(volume)}
        </span>
      </span>
      <span className="chain-tile-figures num">
        <span title="Trades, last 24 hours">
          <Icon name="trades" size={13} />
          {compact(trades)}
        </span>
        {traders != null && (
          <span title="Traders, last 24 hours">
            <Icon name="buyers" size={13} />
            {compact(traders)}
          </span>
        )}
        <span title="New tokens, last 24 hours">
          <Icon name="rocket" size={13} />
          {compact(newTokens)}
        </span>
      </span>
      {share != null && (
        <span className="chain-tile-share" title={`${Math.round(share * 100)}% of the volume`} aria-hidden="true">
          <span style={{ width: `${share * 100}%` }} />
        </span>
      )}
    </button>
  );
}

/** The whole list, or a chain's page (`from`), which starts on that chain. */
export function Screener({ from }: { from?: ChainId }) {
  const [chain, setChain] = useState<ChainId | "all">(from ?? "all");
  const [preset, setPreset] = useState(0);
  const [sort, setSort] = useState<{ key: Sort; order: "desc" | "asc" }>({ key: PRESETS[0]!.sort, order: "desc" });
  const [minLiq, setMinLiq] = useState(0);
  const [period, setPeriod] = useState<FeeWindow>("1h");
  const [more, setMore] = useState<{ rows: TokenRow[]; next: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const now = useNow(5000);

  const title = from ? `${CHAINS[from].name} tokens: prices and charts | Aurelia Screener` : "Aurelia Screener: live token charts and new launches";
  useEffect(() => void (document.title = title), [title]);

  const p = PRESETS[preset]!;
  const byFees = sort.key === "fees"; // a phone shows fees or volume, whichever ranks the list
  const params = new URLSearchParams({ sort: byFees ? `feesPaid${period}Usd` : sort.key, order: sort.order, limit: "50" });
  if (p.minFees) params.set(`minFeesPaid${period}Usd`, String(p.minFees));
  if (chain !== "all") params.set("chains", chain);
  for (const [k, v] of Object.entries(p.filters)) params.set(k, String(v));
  if (minLiq) params.set("minLiquidityUsd", String(Math.max(minLiq, p.filters.minLiquidityUsd ?? 0)));
  const path = `/tokens?${params}`;

  // A first page refreshes itself; once more pages are loaded the list holds still.
  const { data, error } = useGet<{ tokens: TokenRow[]; next: string | null; total: number }>(path, more ? undefined : 15_000);
  useEffect(() => setMore(null), [path]);

  const rows = [...(data?.tokens ?? []), ...(more?.rows ?? [])];
  const next = more ? more.next : data?.next ?? null;

  const loadMore = async () => {
    if (!next) return;
    setLoadingMore(true);
    try {
      const page = await get<{ tokens: TokenRow[]; next: string | null }>(`${path}&cursor=${encodeURIComponent(next)}`);
      setMore((m) => ({ rows: [...(m?.rows ?? []), ...page.tokens], next: page.next }));
    } finally {
      setLoadingMore(false);
    }
  };

  const sortBy = (key: Sort) =>
    setSort((s) => (s.key === key ? { key, order: s.order === "desc" ? "asc" : "desc" } : { key, order: "desc" }));

  // A function, not a component: a component made in render would remount, and the clicked header would lose focus.
  const head = (children: string, k?: Sort, className = "") =>
    k ? (
      <button key={children} className={`th sortable ${className} ${sort.key === k ? "sorted" : ""}`} onClick={() => sortBy(k)} aria-sort={sort.key === k ? (sort.order === "desc" ? "descending" : "ascending") : "none"}>
        {children}
        {sort.key === k && <span aria-hidden="true">{sort.order === "desc" ? " ↓" : " ↑"}</span>}
      </button>
    ) : (
      <span key={children} className={`th ${className}`}>{children}</span>
    );

  return (
    <div className="page">
      <h1 className="sr-only">{from ? `${CHAINS[from].name} tokens` : "Aurelia Screener"}</h1>
      <ChainStrip chain={chain} onPick={setChain} />

      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Ranking">
          {PRESETS.map((x, i) => (
            <button
              key={x.name}
              title={x.hint}
              className={preset === i ? "seg active" : "seg"}
              onClick={() => {
                setPreset(i);
                setSort({ key: x.sort, order: "desc" });
              }}
            >
              {x.name}
            </button>
          ))}
        </div>
        <label className="select">
          <span className="muted">Fees over</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value as FeeWindow)}>
            {FEE_PERIODS.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="select">
          <span className="muted">Liquidity from</span>
          <select value={minLiq} onChange={(e) => setMinLiq(Number(e.target.value))}>
            {LIQUIDITY.map((v) => (
              <option key={v} value={v}>
                {v ? usd(v) : "any"}
              </option>
            ))}
          </select>
        </label>
        {data && <span className="toolbar-count muted num">{data.total.toLocaleString("en-US")} tokens</span>}
      </div>

      <div className="table screener-table" role="table">
        <div className="tr thead" role="row">
          <span className="th rank">#</span>
          <span className="th token-col">Token</span>
          {head("Price", "priceUsd")}
          {head("Age", "createdAt", "hide-sm")}
          {head("Txns", "trades24h", "hide-sm")}
          {head("Volume", "volume24hUsd", byFees ? "hide-sm" : "")}
          {head(`Fees ${period.toUpperCase()}`, "fees", byFees ? "" : "hide-sm")}
          {head("Makers", undefined, "hide-md")}
          {head("5M", undefined, "hide-md")}
          {head("1H", undefined, "hide-sm")}
          {head("6H", undefined, "hide-md")}
          {head("24H", "change24h")}
          {head("Liquidity", "liquidityUsd", "hide-sm")}
          {head("MCap", "marketCapUsd", "hide-sm")}
        </div>
        {rows.map((t, i) => (
          <A key={`${t.chain}:${t.token.address}`} href={tokenHref(t.chain, t.token.address)} className="tr row">
            <span className="td rank muted num">{i + 1}</span>
            <span className="td token-col">
              <TokenLogo src={t.token.logo} symbol={t.token.symbol} chain={t.chain} size={30} />
              <span className="token-names">
                <strong className="ellipsis">{t.token.symbol ?? "?"}</strong>
                <span className="muted ellipsis">{t.token.name}</span>
              </span>
              {t.launchpad && !t.launchpad.complete && (
                <span className="badge">
                  <Launchpad id={t.launchpad.id} />
                </span>
              )}
              {!!t.flags?.length && (
                <span className="badge warn" title={`Risk flags: ${t.flags.join(", ").replace(/_/g, " ")}`}>
                  {t.flags.length} {t.flags.length === 1 ? "flag" : "flags"}
                </span>
              )}
            </span>
            <span className="td num">{price(t.priceUsd)}</span>
            <span className="td num muted hide-sm">{t.createdAt ? age(t.createdAt, now) : "–"}</span>
            <span className="td num hide-sm">{compact(t.trades24h)}</span>
            <span className={byFees ? "td num hide-sm" : "td num"}>{usd(t.volume24hUsd)}</span>
            <span className={byFees ? "td num" : "td num hide-sm"}>{usd(t.feesPaidUsd?.[period])}</span>
            <span className="td num hide-md">{compact(t.traders24h)}</span>
            <span className="td hide-md">
              <Change value={t.change["5m"]} />
            </span>
            <span className="td hide-sm">
              <Change value={t.change["1h"]} />
            </span>
            <span className="td hide-md">
              <Change value={t.change["6h"]} />
            </span>
            <span className="td">
              <Change value={t.change["24h"]} />
            </span>
            <span className="td num hide-sm">{usd(t.liquidityUsd)}</span>
            <span className="td num hide-sm">{usd(t.marketCapUsd)}</span>
          </A>
        ))}
        {!data && !error && Array.from({ length: 12 }, (_, i) => <div key={i} className="tr row skeleton" />)}
        {error && <p className="table-note">The list could not be loaded ({error.message}). It retries in a few seconds.</p>}
        {data && !rows.length && <p className="table-note muted">No token matches these filters.</p>}
      </div>
      {next && (
        <button className="pill ghost more" onClick={loadMore} disabled={loadingMore}>
          {loadingMore ? "Loading…" : "Show more"}
        </button>
      )}
    </div>
  );
}
